'use client';
import { FC, useEffect, useRef } from 'react';
import { Heading } from '@/components';
import ProductCardLoading from '@/components/Loading/ProductCard';
import ProductCardWizzy from '@/components/ProductCardWizzy';
import { googleDataLayerEventPush } from '@/utils/helper';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useCart } from '@/context/cartContext';

interface CartRecommendationPropTypes {
  toggleCartSidebar: (value: boolean) => void;
  loading: boolean;
}

const CartRecommendations: FC<CartRecommendationPropTypes> = ({
  toggleCartSidebar,
  loading,
}) => {
  const ref = useRef<any>(null);

  const { recommendations } = useCart();

  useEffect(() => {
    const entries = recommendations ? Object.entries(recommendations) : [];
    const allItems = entries.flatMap(([, value]) => value?.products || []);
    if (allItems && allItems.length > 0) {
      googleDataLayerEventPush({
        eventName: 'view_item_list',
        eventParameters: {
          item_list_id: 'ARGOID_CART_TRENDING',
          item_list_name: 'Trending Products (Cart)',
          items: allItems,
        },
      });
    }
  }, [Object.keys(recommendations || {}).length]);

  const entries = recommendations ? Object.entries(recommendations) : [];
  if (!recommendations || (!loading && entries.length === 0)) {
    return <></>;
  }
  return (
    <>
      <Heading className='text-lg font-bold mb-4'>Recommended For You</Heading>
      <Tabs defaultValue={entries[0]?.[0]} className='w-full'>
        <TabsList className='flex justify-start gap-3 bg-transparent py-2 h-auto mb-5 overflow-x-auto scrollbar-hide max-w-full mx-auto'>
          <div className='flex gap-3 min-w-max mx-auto w-full px-4'>
            {entries.map(([urlKey, entry]) => (
              <TabsTrigger
                key={urlKey}
                value={urlKey}
                className='flex items-center justify-center gap-1.5 rounded-full px-6 py-2 text-sm font-medium whitespace-nowrap shrink-0 border border-gray-300 bg-transparent text-gray-700 hover:bg-gray-50 transition-all duration-200'>
                {entry?.name || urlKey}
              </TabsTrigger>
            ))}
          </div>
        </TabsList>
        <div>
          {loading && entries.length === 0 ? (
            <ProductCardLoading />
          ) : (
            <>
              {entries.map(([urlKey, entry]) => (
                <TabsContent
                  key={urlKey}
                  value={urlKey}
                  className='block w-full'>
                  <div className='mb-4 pl-4'>
                    <div
                      className='overflow-x-auto flex w-full scrollbar-hide'
                      ref={ref}>
                      {(entry?.products || []).map(
                        (item: any, index: number) => {
                          return (
                            <div className='px-2' key={index}>
                              <ProductCardWizzy
                                item={item}
                                key={index}
                                toggleCartSidebar={toggleCartSidebar}
                                eventMetadata={{
                                  sectionName: `Cart Recommendations - ${entry?.name || urlKey}`,
                                  rank: index + 1,
                                }}
                                dataLayerSource='ARGOID_CART_RECOMMENDATIONS'
                                classNames={{
                                  root: 'max-md:w-[12rem] w-[14rem] mr-4',
                                  textContainer: 'lg:h-11 h-14',
                                  button: 'py-[0.3rem] max-md:py-[0.25rem]',
                                }}
                              />
                            </div>
                          );
                        }
                      )}
                    </div>
                  </div>
                </TabsContent>
              ))}
            </>
          )}
        </div>
      </Tabs>
    </>
  );
};

export default CartRecommendations;
